import { useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";

import { format, parseISO } from "date-fns";

import {
  CircularProgress,
  Box,
  Chip,
  Typography,
  Button,
  Skeleton,
  Checkbox,
} from "@mui/material";
import { useSnackbar } from "notistack";

import {
  fetchPendingInvoices,
  selectInvoice,
} from "../../../../redux/slices/invoicesSlice";
import { apiGet } from "../../../../services/api/apiClient";
import CartPaymentDialog from "./CartPaymentDialog";
import InvoiceDocPreview from "./InvoiceDocPreview";

type InvoiceDocLink = {
  id: string;
  displayName: string;
  sectionKey: string;
  groupLabel?: string;
  docType?: string;
  extension?: string;
};

const statusCodes: Record<number, string> = {
  0: "Paid in full",
  1: "Open",
  2: "Partial payment",
  4: "Scheduled",
};

const statusColor: Record<number, "success" | "warning" | "info" | "secondary" | "default"> = {
  0: "success",
  1: "warning",
  2: "info",
  4: "secondary",
};

const homeFilterStyle = {
  height: 32,
  border: "1px solid var(--tpc-300)",
  borderRadius: 0,
  padding: "0 8px",
  fontFamily: '"Brandon Grotesque", sans-serif',
  fontSize: 12,
  background: "#ffffff",
};

const PendingInvoices = () => {
  const {
    fetchPendingInvoicesStatus,
    invoiceChargeStatus,
    pendingInvoices,
    selectedInvoice,
  } = useAppSelector((state) => state.invoices) as any;

  const { updateProjectPaymentMethodStatus } = useAppSelector(
    (state) => state.projects
  ) as any;
  const { projects, selectedProject, fetchProjectsStatus } = useAppSelector(
    (state) => state.projects
  ) as any;

  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [previewDoc, setPreviewDoc] = useState<InvoiceDocLink | null>(null);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("all");
  const [invoiceFrom, setInvoiceFrom] = useState("");
  const [invoiceTo, setInvoiceTo] = useState("");
  const displayProject = selectedProject || projects?.[0] || null;
  const [docLinks, setDocLinks] = useState<Record<string, InvoiceDocLink>>({});

  // ── Multi-invoice ("shopping cart") selection ──
  const [cartIds, setCartIds] = useState<string[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const { cartChargeStatus, cartChargeError } = useAppSelector(
    (state) => state.invoices
  ) as any;

  const toggleCart = (invoiceId: string) => {
    setCartIds((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId],
    );
  };

  // Keep a ref so the sync effect can read selectedInvoice without depending on it
  const selectedInvoiceRef = useRef(selectedInvoice);
  selectedInvoiceRef.current = selectedInvoice;

  useEffect(() => {
    dispatch(fetchPendingInvoices());
  }, [dispatch]);

  useEffect(() => {
    if (invoiceChargeStatus === "succeeded") {
      dispatch(fetchPendingInvoices());
    }
    if (updateProjectPaymentMethodStatus === "succeeded") {
      dispatch(fetchPendingInvoices());
    }
  }, [invoiceChargeStatus, dispatch, updateProjectPaymentMethodStatus]);

  // Cart payment outcome: on success clear the cart and refresh; on failure
  // surface the backend error.
  useEffect(() => {
    if (cartChargeStatus === "succeeded") {
      enqueueSnackbar("Payment processed successfully", { variant: "success" });
      setCartOpen(false);
      setCartIds([]);
      dispatch(fetchPendingInvoices());
    }
    if (cartChargeStatus === "failed") {
      enqueueSnackbar(cartChargeError, { variant: "error" });
    }
  }, [cartChargeStatus, cartChargeError, dispatch, enqueueSnackbar]);

  const loading = useMemo(
    () =>
      fetchPendingInvoicesStatus === "loading" ||
      fetchProjectsStatus === "loading" ||
      invoiceChargeStatus === "loading",
    [fetchPendingInvoicesStatus, fetchProjectsStatus, invoiceChargeStatus]
  );

  const displayInvoices = useMemo(() => {
    if (!pendingInvoices) return [];
    // Wait for the projects list to resolve: until then there is no project
    // to scope to, and showing every invoice across projects is wrong.
    if (!displayProject) return [];

    const needle = invoiceSearch.trim().toLowerCase();
    const filtered = pendingInvoices.filter((invoice: any) => {
      const invoiceProjectId = invoice?.projectId || invoice?.databaseId;
      const invoiceProjectName = invoice?.projectName || invoice?.customerName;
      const inProject =
        invoiceProjectId === displayProject.id ||
        invoiceProjectName === displayProject.name;
      if (!inProject) return false;

      if (invoiceStatus !== "all" && String(invoice?.paymentStatus) !== invoiceStatus) {
        return false;
      }
      if (needle) {
        const haystack = [
          invoice?.invoiceNumber,
          invoice?.projectName,
          invoice?.description,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      const timestamp = Date.parse(invoice?.invoiceDate || invoice?.date || "");
      if (invoiceFrom && (!Number.isFinite(timestamp) || timestamp < Date.parse(invoiceFrom))) {
        return false;
      }
      if (
        invoiceTo &&
        (!Number.isFinite(timestamp) ||
          timestamp > Date.parse(invoiceTo) + 86_400_000 - 1)
      ) {
        return false;
      }
      return true;
    });

    // Newest invoice first, matching the Financial page.
    return [...filtered].sort((a: any, b: any) => {
      const aDate = Date.parse(a?.invoiceDate || a?.date || "") || 0;
      const bDate = Date.parse(b?.invoiceDate || b?.date || "") || 0;
      if (aDate !== bDate) return bDate - aDate;
      return String(b?.invoiceNumber || "").localeCompare(
        String(a?.invoiceNumber || ""),
        undefined,
        { numeric: true },
      );
    });
  }, [
    pendingInvoices,
    displayProject,
    invoiceSearch,
    invoiceStatus,
    invoiceFrom,
    invoiceTo,
  ]);

  // A draw saved in Invoices & Budgets carries the invoice number in its name;
  // ask the library which invoices have one so the row can offer it.
  const numbers = useMemo(
    () =>
      Array.from(
        new Set(
          displayInvoices
            .map((invoice: any) => String(invoice?.invoiceNumber || "").trim())
            .filter(Boolean),
        ),
      ).sort(),
    [displayInvoices],
  );
  const numbersParam = numbers.join(",");

  useEffect(() => {
    if (!displayProject?.id || !numbersParam) {
      setDocLinks({});
      return;
    }
    let cancelled = false;
    apiGet({
      endpoint: "/document-library/invoice-links",
      params: { projectId: displayProject.id, numbers: numbersParam },
      withAuth: true,
    })
      .then((data: { links?: Record<string, InvoiceDocLink> }) => {
        if (!cancelled) setDocLinks(data?.links || {});
      })
      .catch(() => {
        if (!cancelled) setDocLinks({});
      });
    return () => {
      cancelled = true;
    };
  }, [displayProject?.id, numbersParam]);

  const cartInvoices = useMemo(
    () => displayInvoices.filter((inv: any) => cartIds.includes(inv.id)),
    [displayInvoices, cartIds],
  );

  const cartTotal = useMemo(
    () =>
      cartInvoices.reduce((sum: number, inv: any) => {
        const payable =
          inv?.calculatedAmountDue ??
          (inv?.amountDue != null && inv?.scheduledAmount != null
            ? inv.amountDue - inv.scheduledAmount
            : inv?.amount ?? 0);
        return sum + (payable || 0);
      }, 0),
    [cartInvoices],
  );

  useEffect(() => {
    if (fetchPendingInvoicesStatus === "succeeded" && selectedInvoiceRef.current !== null) {
      const currentId = selectedInvoiceRef.current?.id;
      const invoiceToReplace = displayInvoices.find((invoice: any) => invoice?.id === currentId);
      if (invoiceToReplace) {
        dispatch(selectInvoice(invoiceToReplace));
      } else {
        dispatch(selectInvoice(null));
      }
    }
  }, [dispatch, fetchPendingInvoicesStatus, displayInvoices]);

  return (
    <Box>

      {/* Section Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Brandon Grotesque", sans-serif',
            fontWeight: 700,
            fontSize: "0.85rem",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: "var(--on-surface)",
          }}
        >
          Outstanding Invoices
        </Typography>

        {fetchPendingInvoicesStatus === "loading" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: "var(--outline)",
                fontSize: "0.7rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              Synchronizing...
            </Typography>
            <CircularProgress size={14} sx={{ color: "var(--outline)" }} />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "center",
          mb: 2,
        }}
      >
        <input
          type="search"
          aria-label="Search outstanding invoices"
          placeholder="Search outstanding invoices"
          value={invoiceSearch}
          onChange={(event) => setInvoiceSearch(event.target.value)}
          style={{ ...homeFilterStyle, minWidth: 200 }}
        />
        <select
          aria-label="Outstanding invoice status"
          value={invoiceStatus}
          onChange={(event) => setInvoiceStatus(event.target.value)}
          style={homeFilterStyle}
        >
          <option value="all">All statuses</option>
          <option value="1">Open</option>
          <option value="2">Partial</option>
          <option value="4">Scheduled</option>
        </select>
        <input
          type="date"
          aria-label="Invoice from date"
          value={invoiceFrom}
          onChange={(event) => setInvoiceFrom(event.target.value)}
          style={homeFilterStyle}
        />
        <input
          type="date"
          aria-label="Invoice to date"
          value={invoiceTo}
          onChange={(event) => setInvoiceTo(event.target.value)}
          style={homeFilterStyle}
        />
        {(invoiceSearch || invoiceFrom || invoiceTo || invoiceStatus !== "all") && (
          <Button
            size="small"
            onClick={() => {
              setInvoiceSearch("");
              setInvoiceFrom("");
              setInvoiceTo("");
              setInvoiceStatus("all");
            }}
          >
            Clear
          </Button>
        )}
      </Box>

      {/* Invoice List Container */}
      <Box
        sx={{
          backgroundColor: "var(--surface-container-lowest, #ffffff)",
          borderRadius: 0,
          overflow: "hidden",
        }}
      >
        {fetchPendingInvoicesStatus === "loading" || fetchProjectsStatus === "loading" ? (
          <InvoiceListSkeleton />
        ) : displayInvoices && displayInvoices.length > 0 ? (
          displayInvoices.map((invoice: any, index: number) => {
            const paymentStatus =
              statusCodes[invoice?.paymentStatus] || "Unknown";
            const paymentStatusClr =
              statusColor[invoice?.paymentStatus] || "default";
            const docLink = docLinks[String(invoice?.invoiceNumber || "").trim()];

            return (
              <Box
                key={invoice.id}
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { xs: "stretch", md: "center" },
                  justifyContent: "space-between",
                  px: { xs: 2.5, md: 3.5 },
                  py: 2.5,
                  gap: { xs: 1.25, md: 2 },
                  transition: "background-color 0.2s ease",
                  backgroundColor:
                    index % 2 === 0
                      ? "transparent"
                      : "var(--surface-container-low, #f7f7f7)",
                  "&:hover": {
                    backgroundColor: "var(--surface-container, #efefef)",
                  },
                  cursor: "pointer",
                }}
                onClick={() => dispatch(selectInvoice(invoice))}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    minWidth: 0,
                    flex: 1,
                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  {/* Cart selection checkbox */}
                  <Checkbox
                    size="small"
                    checked={cartIds.includes(invoice.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleCart(invoice.id)}
                    disabled={loading}
                    sx={{ p: 0.5 }}
                  />
                {/* Left: Invoice Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Brandon Grotesque", sans-serif',
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        color: "var(--on-surface)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {invoice.projectName || "Invoice"}
                    </Typography>
                    <Chip
                      label={paymentStatus}
                      color={paymentStatusClr}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: "0.6rem",
                        fontWeight: 500,
                        letterSpacing: "0.05em",
                        borderRadius: 0,
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "var(--outline)",
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    {invoice.invoiceNumber}
                    {invoice.invoiceDate &&
                      ` · ${format(parseISO(invoice.invoiceDate), "MMM dd, yyyy")}`}
                    {invoice.description && ` · ${invoice.description}`}
                  </Typography>
                </Box>
                </Box>

                {/* Right: Amount + Actions */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                    gap: { xs: 1, md: 3 },
                    flexShrink: 0,
                    width: { xs: "100%", md: "auto" },
                    pl: { xs: 5, md: 0 },
                  }}
                >
                  <Box
                    sx={{
                      textAlign: { xs: "left", sm: "right" },
                      minWidth: { sm: 110 },
                      width: { xs: "100%", sm: "auto" },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Brandon Grotesque", sans-serif',
                        fontWeight: 700,
                        fontSize: "0.95rem",
                        color: "var(--on-surface)",
                      }}
                    >
                      {invoice?.amount?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      justifyContent: { xs: "flex-start", md: "flex-end" },
                      width: { xs: "100%", md: 300 },
                    }}
                  >
                    {docLink && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewDoc(docLink);
                        }}
                        title={docLink.displayName}
                        sx={{
                          fontFamily: '"Brandon Grotesque", sans-serif',
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: "var(--on-surface-variant)",
                          px: 2,
                          minWidth: "auto",
                          borderRadius: 0,
                          "&:hover": {
                            backgroundColor: "var(--surface-container-high)",
                          },
                        }}
                      >
                        {docLink.docType === "draw" ? "View draw" : "View document"}
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="text"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(selectInvoice(invoice));
                      }}
                      disabled={loading}
                      sx={{
                        fontFamily: '"Brandon Grotesque", sans-serif',
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: "var(--on-surface-variant)",
                        px: 2,
                        minWidth: "auto",
                        borderRadius: 0,
                        "&:hover": {
                          backgroundColor: "var(--surface-container-high)",
                        },
                      }}
                    >
                      Details
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(selectInvoice(invoice));
                      }}
                      disabled={loading}
                      sx={{
                        fontFamily: '"Brandon Grotesque", sans-serif',
                        fontSize: "0.7rem",
                        fontWeight: 500,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        backgroundColor: "var(--primary)",
                        color: "var(--on-primary)",
                        px: 2.5,
                        minWidth: "auto",
                        borderRadius: 0,
                        "&:hover": {
                          backgroundColor: "var(--primary-dim)",
                        },
                      }}
                    >
                      Pay
                    </Button>
                  </Box>
                </Box>
              </Box>
            );
          })
        ) : (
          <Box sx={{ px: 3.5, py: 4 }}>
            <Typography
              sx={{
                fontSize: "0.85rem",
                color: "var(--on-surface-variant)",
              }}
            >
              No pending or unpaid invoices were found for this project.
            </Typography>
          </Box>
        )}
        {cartIds.length > 0 && (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              px: { xs: 2.5, md: 3.5 },
              py: 1.5,
              borderTop: "1px solid var(--surface-container-high, #dcdcdc)",
              backgroundColor: "var(--surface-container-lowest, #ffffff)",
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
              {cartIds.length}{" "}
              {cartIds.length === 1 ? "invoice" : "invoices"} selected ·{" "}
              {cartTotal.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                onClick={() => setCartIds([])}
                disabled={loading}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={loading}
                onClick={() => setCartOpen(true)}
                sx={{
                  fontFamily: '"Brandon Grotesque", sans-serif',
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  backgroundColor: "var(--primary)",
                  color: "var(--on-primary)",
                  borderRadius: 0,
                  "&:hover": {
                    backgroundColor: "var(--primary-dim)",
                  },
                }}
              >
                Pay selected
              </Button>
            </Box>
          </Box>
        )}
        <CartPaymentDialog
          open={cartOpen}
          invoices={cartInvoices}
          project={displayProject}
          onClose={() => setCartOpen(false)}
        />
      </Box>

      <InvoiceDocPreview
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </Box>
  );
};

/** Skeleton for list-style loading state */
const InvoiceListSkeleton = () => (
  <>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3.5,
          py: 2.5,
          backgroundColor:
            i % 2 === 0
              ? "transparent"
              : "var(--surface-container-low, #f7f7f7)",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="55%" height={20} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width="35%" height={14} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="rounded" width={60} height={28} sx={{ borderRadius: 0 }} />
        </Box>
      </Box>
    ))}
  </>
);

export default PendingInvoices;
